import clsx from "clsx";
import type { JSX } from "react";
import { useCallback, useRef } from "react";
import AsyncSelect from "react-select/async";
import type { MultiValue } from "react-select";
import type { DictType } from "@adlywise/shared";
import { searchDict } from "@/features/analytics/dictApi";
import type { ChoiceOption } from "./types";

const SEARCH_DEBOUNCE_MS = 250;

const controlClassName = (isFocused: boolean): string =>
  clsx(
    "min-h-10 rounded-(--radius-md) border bg-(--surface-1) px-3 text-[13px] text-(--text-primary) transition",
    isFocused
      ? "border-(--brand-primary)"
      : "border-(--border-subtle) hover:border-(--border-strong)",
  );

const optionClassName = (isSelected: boolean, isFocused: boolean): string =>
  clsx("cursor-pointer rounded-(--radius-md) px-3 py-2 text-[13px] transition", {
    "bg-(--pastel-violet) text-(--brand-primary) font-semibold": isSelected,
    "bg-(--surface-2) text-(--text-primary)": !isSelected && isFocused,
    "text-(--text-secondary)": !isSelected && !isFocused,
  });

const dropdownIndicatorClassName = (isFocused: boolean): string =>
  clsx("px-1 text-(--text-secondary) transition", {
    "text-(--text-primary)": isFocused,
  });

export const DictMultiSelectField = ({
  label,
  dictType,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  dictType: DictType;
  value: ChoiceOption<number>[];
  onChange: (nextValue: ChoiceOption<number>[]) => void;
  placeholder?: string;
}): JSX.Element => {
  const debounceRef = useRef<number | undefined>(undefined);

  const loadOptions = useCallback(
    (inputValue: string): Promise<ChoiceOption<number>[]> => {
      if (debounceRef.current !== undefined) {
        window.clearTimeout(debounceRef.current);
      }

      return new Promise((resolve, reject) => {
        debounceRef.current = window.setTimeout(() => {
          searchDict(dictType, inputValue)
            .then((entries) => {
              resolve(
                entries.map((entry) => ({
                  value: entry.id,
                  label: entry.name,
                })),
              );
            })
            .catch(reject);
        }, SEARCH_DEBOUNCE_MS);
      });
    },
    [dictType],
  );

  return (
    <label className="block space-y-2">
      <span className="text-[12px] font-semibold text-(--text-secondary)">
        {label}
      </span>
      <AsyncSelect<ChoiceOption<number>, true>
        isMulti
        unstyled
        cacheOptions
        defaultOptions
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        tabSelectsValue={false}
        loadOptions={loadOptions}
        value={value}
        placeholder={placeholder ?? "Search and select..."}
        loadingMessage={() => "Searching…"}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? "No matching options" : "Type to search"
        }
        menuPlacement="auto"
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 1600,
          }),
        }}
        onChange={(nextValue: MultiValue<ChoiceOption<number>>) => {
          onChange([...nextValue]);
        }}
        classNames={{
          control: (state) => controlClassName(state.isFocused),
          valueContainer: () => "gap-1.5 py-1.5",
          placeholder: () => "text-[13px] text-(--text-muted)",
          input: () => "m-0 p-0 text-[13px] text-(--text-primary)",
          menu: () =>
            "mt-2 overflow-hidden rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1)",
          menuList: () => "max-h-[248px] p-1.5",
          option: (state) => optionClassName(state.isSelected, state.isFocused),
          multiValue: () => "rounded-(--radius-sm) bg-(--pastel-violet)",
          multiValueLabel: () =>
            "px-2 py-0.5 text-[12px] font-semibold text-(--brand-primary)",
          multiValueRemove: () =>
            "flex items-center rounded-r-(--radius-sm) px-1.5 text-(--brand-primary) transition hover:bg-(--brand-primary) hover:text-(--brand-primary-contrast)",
          indicatorsContainer: () => "gap-1",
          dropdownIndicator: (state) =>
            dropdownIndicatorClassName(state.isFocused),
          clearIndicator: () =>
            "px-1 text-(--text-muted) transition hover:text-(--text-primary)",
          indicatorSeparator: () => "hidden",
          noOptionsMessage: () => "px-3 py-2 text-[13px] text-(--text-muted)",
          loadingMessage: () => "px-3 py-2 text-[13px] text-(--text-muted)",
        }}
      />
    </label>
  );
};
